import React, { ReactNode } from 'react';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import { skipToken } from '@reduxjs/toolkit/query';
import { generateWeekDays } from '@helpers/generateWeekDays';
import { WeekSlot } from '@pages/Schedule/components/Week/WeekSlot';
import { useScheduleContext } from '@pages/Schedule/context/useScheduleContext';
import { useFetchAllPhysiciansQuery } from '@api/physicians/physiciansAPI';
import { calcAmountByProperty } from '@pages/Schedule/utils/calcAmountByProperty';
import { TEventTypes } from '@pages/Schedule/components/Slot/types';
import { TAppointment } from '@api/appointments/types';
import { IScheduleResponse } from '@api/schedule/types';
import { getBlackourLocationForDate } from '@pages/Schedule/utils/getBlackoutsLocationForDate';
import { useFetchLocationBlackoutsQuery } from '@api/blackout/blackoutAPI';
import { getWeekDates } from '@pages/Schedule/utils/getWeekDates';

interface IWeekBodyProps {
  appointments: IScheduleResponse | undefined;
}

export const WeekBody = ({ appointments }: IWeekBodyProps) => {
  const { selectedDate, selectedPhysician, selectedLocationId } = useScheduleContext();

  const tableData = generateWeekDays(selectedDate);

  const scheduleData: TAppointment[] | undefined = appointments?.data;

  const { data: locationsBlackout } = useFetchLocationBlackoutsQuery(
    !selectedLocationId ? skipToken : selectedLocationId,
  );

  const selectedWeekDates = getWeekDates(selectedDate);

  const weekBlackoutLocations = selectedWeekDates.map((date) =>
    getBlackourLocationForDate(date, locationsBlackout?.data),
  );

  const isCurrentBlackout = weekBlackoutLocations.some((blackoutLocationsSlots) =>
    blackoutLocationsSlots?.some((slot) => slot.locationId === selectedLocationId),
  );

  const { data: AllPhysicians } = useFetchAllPhysiciansQuery();

  const tableBody = tableData.reduce<ReactNode[]>((acc, currentWeekDay, rowIndex) => {
    const physicians = AllPhysicians?.data.results
      .filter((physician) => physician?.schedule?.locationId === selectedLocationId)
      .filter((physician) =>
        selectedPhysician?.length ? physician.id === selectedPhysician : physician,
      )
      .map((physician) => physician.id);

    const tableRow = physicians?.map((physician, physicianIndex) => {
      const currentDateAppointments = scheduleData?.filter(({ date }) =>
        isSameDay(new Date(date), currentWeekDay.value),
      );

      const currentPhysicianAppointments = currentDateAppointments?.filter(
        ({ trials }) => trials.physicianId === physician,
      );

      const groupedAppointmentsByType = currentPhysicianAppointments?.length
        ? calcAmountByProperty(currentPhysicianAppointments, 'type')
        : [];

      const appointmentsAmount = currentPhysicianAppointments?.length;

      const blackoutSchedule = AllPhysicians?.data.results.find(
        (p) => p.id === physician,
      )?.blackout;

      const currentDateBlackouts = blackoutSchedule?.filter(({ from }) =>
        isSameDay(new Date(from), currentWeekDay.value),
      );

      return (
        <WeekSlot
          key={`week-slot-${physician}-${currentWeekDay.value}-${physicianIndex}`}
          date={currentWeekDay.value}
          appointmentsAmount={appointmentsAmount}
          eventTypes={groupedAppointmentsByType as TEventTypes[]}
          blackoutsInfo={currentDateBlackouts}
        />
      );
    });

    const blackoutLocationsSlots = getBlackourLocationForDate(
      currentWeekDay.value,
      locationsBlackout?.data,
    );

    const currentBlackoutLocation = blackoutLocationsSlots?.find((slot) => {
      if (slot.from && slot.to && slot.startTime) {
        const fromDate = new Date(`${slot.from} ${slot.startTime}`);

        const toDate = new Date(`${slot.to} ${slot.to_time}`);

        return (
          isSameDay(fromDate, currentWeekDay.value) ||
          isSameDay(toDate, currentWeekDay.value) ||
          (isAfter(currentWeekDay.value, fromDate) && isBefore(currentWeekDay.value, toDate))
        );
      }

      return false;
    });

    const isCurrentBlackoutLocations =
      blackoutLocationsSlots?.some((slot) => slot.locationId === selectedLocationId) &&
      currentBlackoutLocation;

    return [
      ...acc,
      <div className="schedule-row table-row" key={`table-row-body${rowIndex}`}>
        <div className="schedule-cell table-cell">{tableData[rowIndex].label}</div>
        {isCurrentBlackout &&
          (isCurrentBlackoutLocations ? (
            <WeekSlot
              key={`week-slot-blackout-location-${currentWeekDay.value}-${rowIndex}`}
              date={currentWeekDay.value}
              blackoutLocation={currentBlackoutLocation}
            />
          ) : (
            <div className="schedule-cell table-cell" />
          ))}

        {tableRow}
      </div>,
    ];
  }, []);

  return <div className="schedule-body table-body">{tableBody}</div>;
};
